/**
 * Mixpanel as a second destination beside Segment.
 *
 * While both run, every `track` / `identify` / `reset` the `Analytics` class sends to Segment is mirrored here
 * with the same event name and payload. One pinned `mixpanel-browser` instance handles events, identity and
 * Session Replay, so replay recordings join to their events natively.
 *
 * Ported from `rips-ui/src/analytics/plugins/mixpanelSdk.ts` (itself from `predictions-ui`); keep them in step.
 */
import type { Config } from 'mixpanel-browser';

import { logToNewRelic } from '../../debug';

import { installMixpanelProxyAuth } from './mixpanelProxyAuth';

/** The slice of the Mixpanel browser SDK this app depends on. */
interface MixpanelSdk {
  track: (eventName: string, properties?: Record<string, unknown>) => void;
  identify: (distinctId: string) => void;
  reset: () => void;
}

/**
 * Fallback when a mode sets a token but forgets the percent: sample low rather than record every session
 * (Mixpanel bills replay per recorded session). An explicit `0` is honoured as "record nothing".
 */
const DEFAULT_RECORD_SESSIONS_PERCENT = 10;

export interface MixpanelSdkConfig {
  token: string;
  recordSessionsPercent: number;
  trackApiHost: string;
}

/** Reads the `VITE_MIXPANEL_*` variables at call time. Trims — a stray newline is not a token. */
export function readMixpanelSdkConfig(): MixpanelSdkConfig {
  const token = String(import.meta.env.VITE_MIXPANEL_TOKEN ?? '').trim();
  const raw = import.meta.env.VITE_MIXPANEL_RECORD_SESSIONS_PERCENT as string | undefined;
  const parsed = raw != null && raw !== '' ? Number(raw) : NaN;
  return {
    token,
    recordSessionsPercent: Number.isFinite(parsed) ? parsed : DEFAULT_RECORD_SESSIONS_PERCENT,
    trackApiHost: String(import.meta.env.VITE_MIXPANEL_TRACK_API_HOST ?? '').trim(),
  };
}

/**
 * The SDK's own defaults, restated because `mixpanel.init` shallow-merges config: a partial `api_routes`
 * REPLACES the whole map and every unlisted endpoint becomes `<host>/undefined`. Our collector serves `/track`
 * with no trailing slash.
 */
const PROXIED_API_ROUTES = {
  track: 'track',
  engage: 'engage/',
  groups: 'groups/',
  record: 'record/',
  flags: 'flags/',
  settings: 'settings/',
};

/**
 * Sends ONLY event tracking through our own domain; profiles, replay, flags and settings fall back to the
 * default `api_host`. Both keys are required — the SDK reads `track` on the batched path and `events` on the
 * unbatched one, and setting one alone silently leaks traffic straight to Mixpanel on the other.
 */
function trackOnlyApiHosts(host: string): Record<string, string> {
  return { track: host, events: host };
}

/** Published when the SDK fails to load, so queued calls drain instead of piling up. */
const noopSdk: MixpanelSdk = {
  track: () => undefined,
  identify: () => undefined,
  reset: () => undefined,
};

/** Browser opt-out signals, sent on every event so BI can exclude these players from ad-partner sends. */
export function getOptOutSignalProps(): { dnt: boolean; gpc: boolean } {
  if (typeof navigator === 'undefined') return { dnt: false, gpc: false };
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return { dnt: nav.doNotTrack === '1', gpc: nav.globalPrivacyControl === true };
}

async function loadAndInit(): Promise<MixpanelSdk | null> {
  const { token, recordSessionsPercent, trackApiHost } = readMixpanelSdkConfig();
  if (!token) return null;

  // The collector authenticates with `x-write-key` = the project token. Installed before init() because the
  // recorder starts sending during it.
  if (trackApiHost) installMixpanelProxyAuth(trackApiHost, token);

  try {
    const { default: mp } = await import('mixpanel-browser');
    const config: Partial<Config> & Record<string, unknown> = {
      // HOST ONLY — the SDK appends its own route segment. Omitted when unset so every endpoint stays default.
      ...(trackApiHost ? { api_hosts: trackOnlyApiHosts(trackApiHost), api_routes: PROXIED_API_ROUTES } : {}),
      record_sessions_percent: recordSessionsPercent,
      record_heatmap_data: true,
      record_mask_all_text: true,
      record_mask_all_inputs: true,
      autocapture: false,
      // We fire our own screen events through `sendEvent`; the SDK must not add a second page view.
      track_pageview: false,
      batch_requests: true,
      // The SDK otherwise sends nothing from browsers with Do Not Track on (~16% of Android Chrome users);
      // Segment ignores DNT, and the two must see the same traffic for the cutover (PLT-911).
      ignore_dnt: true,
      // The SDK otherwise keeps `utm_*` in a cookie and adds them to later visits; Segment reads only the current
      // URL. Also clears values already stored.
      stop_utm_persistence: true,
    };
    mp.init(token, config);
    // Super properties, so the SDK's own events carry them too. Not persisted: re-read on every page load.
    mp.register(getOptOutSignalProps(), { persistent: false });
    return mp;
  } catch {
    logToNewRelic('Mixpanel.init', 'Failed to initialize mixpanel', 'error');
    return noopSdk;
  }
}

let bootstrap: Promise<MixpanelSdk | null> | null = null;
let releaseQueue: (() => void) | null = null;
/** Calls made before `init()` (e.g. `identify` on auth) wait here, in order, until the SDK is started. */
let started: Promise<void> = new Promise((resolve) => {
  releaseQueue = resolve;
});

/** Loads + initialises the SDK exactly once per page and releases the queued calls. */
function bootstrapMixpanelSdk(): Promise<MixpanelSdk | null> {
  bootstrap ??= loadAndInit();
  releaseQueue?.();
  return bootstrap;
}

function run(fn: (mp: MixpanelSdk) => void) {
  if (!readMixpanelSdkConfig().token) return Promise.resolve();
  return started
    .then(() => bootstrapMixpanelSdk())
    .then((mp) => {
      if (mp) fn(mp);
    })
    .catch(() => undefined);
}

export const mixpanel = {
  init: () => {
    if (!readMixpanelSdkConfig().token) return;
    void bootstrapMixpanelSdk();
  },
  track: (eventName: string, params: Record<string, unknown>) => run((mp) => mp.track(eventName, params)),
  identify: (userId: string) => run((mp) => mp.identify(userId)),
  reset: () => run((mp) => mp.reset()),
};

/** Test-only escape hatch. */
export function __resetMixpanelSdkForTests(): void {
  bootstrap = null;
  started = new Promise((resolve) => {
    releaseQueue = resolve;
  });
}
